import { useEffect, useLayoutEffect, useRef } from 'react';
import { useAppSelector } from '@/app/store/store';

interface UseDismissOnBlockingOverlayOpenOptions {
  isOpen: boolean;
  onDismiss: () => void;
  /** Page-local overlays (e.g. PDP store modals) not tracked in global UI state. */
  hasLocalOverlayOpen?: boolean;
}

function isDomBlockingOverlayOpen(): boolean {
  if (document.documentElement.dataset.hamburgerOpen === 'true') {
    return true;
  }

  if (document.querySelector('[data-blocking-overlay="true"]')) {
    return true;
  }

  return Boolean(document.querySelector('[role="dialog"][data-state="open"]'));
}

function shouldDismissForOverlay(
  loginOpen: boolean,
  searchOpen: boolean,
  hasLocalOverlayOpen: boolean
): boolean {
  return loginOpen || searchOpen || hasLocalOverlayOpen || isDomBlockingOverlayOpen();
}

/**
 * Closes a lightweight overlay (e.g. cart-full toast) when a blocking popup/modal opens.
 */
export function useDismissOnBlockingOverlayOpen({
  isOpen,
  onDismiss,
  hasLocalOverlayOpen = false,
}: UseDismissOnBlockingOverlayOpenOptions) {
  const loginOpen = useAppSelector((s) => s.loginUi.isLoginOpen);
  const searchOpen = useAppSelector((s) => s.searchUi.isSearchModalOpen);
  const onDismissRef = useRef(onDismiss);

  useLayoutEffect(() => {
    onDismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!isOpen) return;

    if (shouldDismissForOverlay(loginOpen, searchOpen, hasLocalOverlayOpen)) {
      onDismissRef.current();
      return;
    }

    const observer = new MutationObserver(() => {
      if (shouldDismissForOverlay(loginOpen, searchOpen, hasLocalOverlayOpen)) {
        onDismissRef.current();
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-hamburger-open'],
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state', 'data-blocking-overlay', 'aria-hidden', 'style'],
    });

    return () => observer.disconnect();
  }, [isOpen, loginOpen, searchOpen, hasLocalOverlayOpen]);
}
